import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Flag, EyeOff, Check, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REASON_LABEL = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate: 'Hate speech',
  inappropriate: 'Inappropriate',
  other: 'Other',
};

export default function ReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [tab, setTab] = useState('pending');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const fetchReports = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports?status_filter=${status}`, { withCredentials: true });
      setReports(data);
    } catch (err) {
      toast.error('Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchReports(tab);
  }, [fetchReports, tab]);

  const act = async (reportId, action) => {
    setActing(reportId);
    try {
      await axios.post(`${API}/admin/reports/${reportId}/action`, { action }, { withCredentials: true });
      toast.success(action === 'hide' ? 'Content hidden' : 'Report dismissed');
      await fetchReports(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="reports-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <Flag className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Moderation</h1>
            <p className="text-sm text-stone-500">Review reports from your family members.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList className="bg-stone-100 rounded-full p-1">
            <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="resolved" className="rounded-full" data-testid="tab-resolved">Resolved</TabsTrigger>
            <TabsTrigger value="dismissed" className="rounded-full" data-testid="tab-dismissed">Dismissed</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="text-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" />
              </div>
            ) : reports.length === 0 ? (
              <div className="card-soft text-center py-10" data-testid="reports-empty">
                <ShieldCheck className="w-10 h-10 text-teal-500 mx-auto mb-3" />
                <p className="text-stone-600 font-medium">
                  {tab === 'pending' ? 'Nothing to review — nice and calm in here.' : `No ${tab} reports.`}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="reports-list">
                {reports.map((r) => (
                  <div key={r.id} className="card-soft p-4" data-testid={`report-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                        <ShieldAlert className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="secondary" className="bg-red-50 text-red-700 hover:bg-red-50">
                            {REASON_LABEL[r.reason] || r.reason}
                          </Badge>
                          <span className="text-xs text-stone-500">
                            reported {r.target_type} · {format(parseISO(r.created_at), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm text-stone-800 line-clamp-3 mb-1">
                          {r.target_snippet || <em className="text-stone-400">(no text)</em>}
                        </p>
                        {r.details && (
                          <p className="text-xs text-stone-500 italic mb-2">"{r.details}"</p>
                        )}
                        <p className="text-xs text-stone-500">Reported by {r.reporter_name || 'a family member'}</p>
                      </div>
                    </div>

                    {r.status === 'pending' && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
                        <Button
                          size="sm"
                          onClick={() => act(r.id, 'hide')}
                          disabled={acting === r.id}
                          className="bg-red-600 hover:bg-red-700 text-white rounded-full flex-1"
                          data-testid={`hide-${r.id}`}
                        >
                          <EyeOff className="w-4 h-4 mr-1.5" /> Hide content
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => act(r.id, 'dismiss')}
                          disabled={acting === r.id}
                          className="rounded-full flex-1"
                          data-testid={`dismiss-${r.id}`}
                        >
                          <Check className="w-4 h-4 mr-1.5" /> Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <MobileNav />
    </div>
  );
}
